import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PlusCircle, Archive } from "lucide-react";
import FormCard from "@/components/dashboard/FormCard";
import EmptyFormState from "@/components/dashboard/EmptyFormState";
import { motion } from "framer-motion";
import { staggerContainer } from "@/utils/animations";
import { Form } from "@/types";
import { useState } from "react";
import { useSubscription } from "@/hooks/subscription";
import { toast } from "sonner";

interface FormsSectionProps {
  forms: Form[];
  isLoading: boolean;
}

const FormsSection = ({ forms, isLoading }: FormsSectionProps) => {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState(false);
  const { isSubscribed } = useSubscription();

  const createNewForm = () => {
    // Redirect to pricing if not subscribed
    if (!isSubscribed) {
      toast.info("Start your 7-day free trial to create investor forms");
      navigate("/pricing");
      return;
    }
    
    // If subscribed or on trial, navigate to forms page with create=true param
    console.log("FormsSection: Redirecting to forms page with create=true param");
    navigate("/forms?create=true");
  };
  
  // Filter forms by archive status
  const filteredForms = forms.filter(form => 
    showArchived ? form.isArchived : !form.isArchived
  );

  return (
    <div className="lg:col-span-2">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold">Your Forms 📋</h2>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowArchived(!showArchived)}
            className={showArchived ? "bg-muted/50" : ""}
          >
            <Archive className="w-3.5 h-3.5 mr-1" />
            {showArchived ? "Viewing Archived" : "Show Archived"}
          </Button>
        </div>
        <Button 
          onClick={createNewForm} 
          className="font-medium"
          variant="outline"
        >
          <PlusCircle className="w-4 h-4 mr-2" />
          Create Form
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-40 rounded-lg bg-muted/50 animate-pulse w-full"
            ></div>
          ))}
        </div>
      ) : filteredForms && filteredForms.length > 0 ? (
        <motion.div 
          {...staggerContainer}
          className="grid gap-4"
        >
          {filteredForms.map((form, index) => (
            <FormCard key={form.id} form={form} index={index} />
          ))}
        </motion.div>
      ) : showArchived ? (
        <div className="text-center py-12 border rounded-lg bg-muted/20">
          <Archive className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No archived forms</h3>
          <p className="text-muted-foreground mb-4">
            You don't have any archived forms yet. Archived forms will appear here.
          </p>
          <Button variant="outline" onClick={() => setShowArchived(false)}>
            View Active Forms
          </Button>
        </div>
      ) : (
        <EmptyFormState />
      )}
    </div>
  );
};

export default FormsSection;
